import Loader from '@/components/Loader';
import BaseLayout from '@/layout/BaseLayout';
import { trpc } from '@/trpcClient';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  batchActionTypeLabels,
  batchRefetchInterval,
  batchStatusClasses,
  batchStatusLabels,
} from './batchActionLabels';

const PAGE_SIZE = 20;

type Column = {
  header: string;
  key: keyof BatchRow;
  render: (value: BatchRow[keyof BatchRow], row: BatchRow) => ReactNode;
};

type BatchRow = NonNullable<ReturnType<typeof useBatchList>['data']>['batches'][number];

function useBatchList(page: number) {
  return useQuery(
    trpc.batchAction.list.queryOptions(
      { page, pageSize: PAGE_SIZE },
      { refetchInterval: (query) => batchRefetchInterval(query.state.data?.batches.map((batch) => batch.status)) }
    )
  );
}

function BatchProgress({ row }: { row: BatchRow }) {
  const { succeeded, failed, notProcessed, interrupted } = row.itemCounts;
  const total = row.affectedCount;

  if (!row.detailsAvailable) {
    return <span className="text-sm text-[var(--gray-primary)]">{total} materiais, detalhes indisponíveis</span>;
  }

  const percent = (count: number) => (total > 0 ? (count / total) * 100 : 0);

  return (
    <div className="flex min-w-[180px] flex-col gap-1">
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={succeeded + failed}
        className="flex h-2 w-full overflow-hidden rounded-full bg-gray-200"
      >
        <div className="bg-green-500" style={{ width: `${percent(succeeded)}%` }} />
        <div className="bg-red-500" style={{ width: `${percent(failed)}%` }} />
      </div>
      <span className="text-xs text-[var(--gray-primary)]">
        {succeeded} concluídos, {failed} falhos de {total}
        {notProcessed + interrupted > 0 && `, ${notProcessed + interrupted} não finalizados`}
      </span>
    </div>
  );
}

const columns: Column[] = [
  {
    header: 'Início',
    key: 'startedAt',
    render: (_, row) => dayjs(row.startedAt).format('DD/MM/YYYY - HH:mm'),
  },
  { header: 'Usuário', key: 'user', render: (_, row) => row.user?.name ?? '-' },
  {
    header: 'Tipo de ação',
    key: 'actionType',
    render: (_, row) => batchActionTypeLabels[row.actionType] ?? row.actionType,
  },
  { header: 'Campanha', key: 'campaign', render: (_, row) => row.campaign?.name ?? '-' },
  {
    header: 'Status',
    key: 'status',
    render: (_, row) => (
      <Badge className={`h-6 rounded-sm px-2 py-[5px] text-xs font-medium ${batchStatusClasses[row.status] ?? ''}`}>
        {batchStatusLabels[row.status] ?? row.status}
      </Badge>
    ),
  },
  { header: 'Progresso', key: 'itemCounts', render: (_, row) => <BatchProgress row={row} /> },
];

export function BatchActionList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useBatchList(page);

  const batches = data?.batches ?? [];

  return (
    <BaseLayout>
      <section className="w-full px-4 md:px-0">
        {isLoading && <Loader message="Carregando ações em lote..." />}
        {isError && <p className="p-4 text-red-500">Erro ao carregar ações em lote.</p>}
        {!isLoading && !isError && batches.length === 0 && <p className="p-4">Nenhuma ação em lote executada.</p>}
        {!isLoading && !isError && batches.length > 0 && (
          <GenericTable
            columns={columns}
            data={batches}
            page={page}
            itemsPerPages={PAGE_SIZE}
            totalItems={data?.total ?? 0}
            onPageChange={setPage}
            onRowClick={(row) => navigate(`/batch-action/${row.uuid}`)}
          />
        )}
      </section>
    </BaseLayout>
  );
}
